/**
 * Calendar dates as ISO "YYYY-MM-DD" strings. ISO dates sort lexicographically,
 * so range checks are plain string comparisons. Arithmetic runs on UTC
 * midnights so a daylight-saving change never shifts a day.
 *
 * Temporal.PlainDate is the native replacement; move these internals onto it
 * once it ships in Safari.
 */

export function parse(date) {
  const [year, month, day] = date.split("-").map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

export function format(date) {
  return date.toISOString().slice(0, 10)
}

export function addDays(date, amount) {
  const result = parse(date)
  result.setUTCDate(result.getUTCDate() + amount)
  return format(result)
}

// Clamps to the end of a shorter month: Jan 31 + 1 month is Feb 28.
export function addMonths(date, amount) {
  const result = parse(startOfMonth(date))
  result.setUTCMonth(result.getUTCMonth() + amount)

  const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate()
  result.setUTCDate(Math.min(dayOfMonth(date), lastDay))
  return format(result)
}

export function dayOfMonth(date) {
  return Number(date.slice(8))
}

export function startOfMonth(date) {
  return `${date.slice(0, 7)}-01`
}

export function isSameMonth(date, other) {
  return date.slice(0, 7) === other.slice(0, 7)
}

// Weeks start on Monday, matching Rails' beginning_of_week default.
export function startOfWeek(date) {
  const daysSinceMonday = (parse(date).getUTCDay() + 6) % 7
  return addDays(date, -daysSinceMonday)
}

export function endOfWeek(date) {
  return addDays(startOfWeek(date), 6)
}

/**
 * The weeks shown for a month: always six, so the calendar keeps one height
 * as it navigates and its popover never re-flips.
 *
 * @returns {string[][]} six weeks of seven dates, Monday first
 */
export function monthWeeks(month) {
  const firstDay = startOfWeek(startOfMonth(month))

  return Array.from({ length: 6 }, (_week, week) =>
    Array.from({ length: 7 }, (_day, day) => addDays(firstDay, week * 7 + day))
  )
}
